 "use client";

import { useEffect, useState } from 'react';
import { useCalendarByResource } from '../useCalendar';
import { useAuthenticated } from '../authenticated/useAuthenticated';
import { useOrganizationSettings } from '../settings/useOrganizationSettings';
import { useRoomReservation } from './useRoomReservation';
import { useTacticHooks } from '../../providers/TacticHooksProvider';

const integrationsUrl = process.env.NEXT_PUBLIC_TACTIC_INTEGRATIONS_URL;

export const useRoomReservationWeb = ({
  isOpen,
  isRoomReservation: mayNeedDelegatedAccess,
  defaultOrganizerId
}: {
  isOpen: boolean;
  isRoomReservation: boolean;
  /** Organizer known before the form reports one, e.g. when editing a reservation. */
  defaultOrganizerId?: string | null;
}) => {
  const { api } = useTacticHooks();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organization = authenticatedUser?.organization;

  const [roomId, setRoomId] = useState<string | null>(null);
  // undefined until the form reports an organizer; null means it reported none
  const [organizerId, setOrganizerId] = useState<string | null | undefined>(
    undefined
  );
  const { calendar } = useCalendarByResource(roomId || null);
  const { data: organizationSettings } = useOrganizationSettings({
    id: organization?.id,
    disabled: !mayNeedDelegatedAccess || !isOpen,
    refetchInterval: 60 * 1000 // 1 min
  });

  // A closed form must not leak its organizer into the next reservation
  useEffect(() => {
    if (!isOpen) {
      setOrganizerId(undefined);
    }
  }, [isOpen]);

  return {
    onRoomChange: (roomId: string | null) => {
      setRoomId(roomId);
    },
    onOrganizerChange: (organizerId: string | null) => {
      setOrganizerId(organizerId);
    },
    ...useRoomReservation({
    isOpen,
    isRoomReservation: mayNeedDelegatedAccess,
    calendar: calendar || null,
    organizationSettings: organizationSettings || null,
    slug: organization?.slug || '',
    integrationsUrl: integrationsUrl || '',
    organizerId:
      organizerId === undefined ? (defaultOrganizerId ?? null) : organizerId,
    currentUserId: authenticatedUser?.user?.id,
    apiClient: api.client.calendarAuth
  })
  }
};
